import React, {Fragment} from 'react';
import {findDOMNode} from 'react-dom';
import BaseComponent from "../../../base-component/base-component";

import ResizeBorder from "./resize-border";
import AlignSelector from "./align-selector/align-selector";

const round = (x, steps) => Math.ceil(x / steps) * steps;

export default ({ config, store }) => WrappedComponent =>
    class BlockResizeableDecorator extends BaseComponent {

        static defaultProps = _.assign({}, {
            horizontal: 'absolute',
            vertical: 'auto',
            resizeSteps: 1,
            defaultWidth: 40
        }, config);

        state = {
            hoverPosition: {},
            clicked: false,
        };

        setEntityData = data => {
            this.props.blockProps.setResizeData(data);
        };

        // used to save the hoverPosition so it can be leveraged to determine if a
        // drag should happen on mousedown
        mouseLeave = () => {
            if (!this.state.clicked) {
                this.setState({ hoverPosition: {} });
            }
        };

        getHoverPosition = (evt) => {
            const tolerance = 6;
            const b = findDOMNode(this).getBoundingClientRect();
            const x = evt.clientX - b.left;
            const y = evt.clientY - b.top;

            const isTop = y < tolerance;
            const isLeft = x < tolerance;
            const isRight = x >= b.width - tolerance;
            const isBottom = y >= b.height - tolerance && y < b.height;

            const canResize = isTop || isLeft || isRight || isBottom;
            return {isTop, isLeft, isRight, isBottom, canResize}
        };

        // Handle mousedown for resizing
        mouseDown = event => {
            // No mouse-hover-position data? Nothing to resize!
            const hoverPosition = this.getHoverPosition(event);
            if (!hoverPosition.canResize) return;

            const { aspectRatio } = this.props.blockProps.resizeData;

            event.preventDefault();
            const { resizeSteps } = this.props;
            const { isTop, isLeft, isRight, isBottom } = hoverPosition;

            const pane = findDOMNode(this);
            // const b = pane.getBoundingClientRect();
            // const aspectRatio = b.height/b.width;
            const startX = event.clientX;
            const startY = event.clientY;
            const startWidth = parseInt(document.defaultView.getComputedStyle(pane).width,10);
            // const startHeight = parseInt(document.defaultView.getComputedStyle(pane).height,10);

            // Do the actual drag operation
            const doDrag = dragEvent => {
                const widthDiff = isLeft ? startX - dragEvent.clientX : dragEvent.clientX - startX;
                const heightDiff = isTop ? startY - dragEvent.clientY : dragEvent.clientY - startY;
                let width;

                if ((isLeft && isTop) || (isRight && isTop) || (isLeft && isBottom) || (isRight && isBottom)) {
                    const heightToWidthDiff = aspectRatio ? heightDiff / aspectRatio : heightDiff;
                    width = startWidth + (heightToWidthDiff + widthDiff)/2
                } else if (isTop || isBottom) {
                    const heightToWidthDiff = aspectRatio ? heightDiff / aspectRatio : heightDiff;
                    width = startWidth + heightToWidthDiff;
                } else if (isLeft || isRight) {
                    width = startWidth + widthDiff;
                }

                const editorComp = store.getEditorRef();
                const editorNode = editorComp.editor;

                const widthPerc = (100 / editorNode.clientWidth) * width;
                // width = Math.min(editorNode.clientWidth, width);

                const newState = {
                    width: resizeSteps ? round(widthPerc, resizeSteps) : widthPerc
                };

                // const height = aspectRatio * newState.width
                // newState.height = resizeSteps ? round(height, resizeSteps) : height;

                dragEvent.preventDefault();

                this.setState(newState);
            };

            // Finished dragging
            const stopDrag = () => {
                document.removeEventListener('mousemove', doDrag, false);
                document.removeEventListener('mouseup', stopDrag, false);

                const { width/*, height*/ } = this.state;
                this.setState({ clicked: false });
                this.setEntityData({ width/*, height*/ });
            };

            document.addEventListener('mousemove', doDrag, false);
            document.addEventListener('mouseup', stopDrag, false);

            this.setState({ clicked: true });
        };

        setAlignment = (styles) => {
            const alignment = this.props.blockProps.resizeData.alignment;
            switch (alignment) {
                case 'left':    styles.marginRight = 'auto'; break;
                case 'right':   styles.marginLeft = 'auto'; break;
                case 'center':
                    styles.marginRight = 'auto';
                    styles.marginLeft = 'auto';
                    break;
            }
        }

        onChangeAlignment = (alignment) => {
            this.setEntityData({alignment});
            this.forceUpdate();
        }

        onRemove = () => {
            if (this.props.blockProps.onRemove) this.props.blockProps.onRemove(this.props.block.key)
        }

        render() {

            const elementProps = _.omit(this.props, ['vertical', 'horizontal', 'style', 'resizeSteps', 'defaultWidth']);

            const data = this.props.contentState.getEntity(this.props.block.getEntityAt(0)).getData();

            const resizeDisabled = data.noPreview || store.getReadOnly();

            let width = this.state.width || this.props.blockProps.resizeData.width || this.props.defaultWidth;
            if (width > 100) width = 100;

            const styles = {
                position: 'relative'
            };

            if (!data.noPreview) {
                styles.width = `${width}%`;
                styles.height = 'auto';
            }

            this.setAlignment(styles);

            const interactionProps = resizeDisabled ? {} : {
                    onMouseDown: this.mouseDown,
                    onMouseLeave: this.mouseLeave,
                };

            const classes = Classes("resize-decorator", {
                'is-resizing'       : this.state.clicked,
                'resize-disabled'   : resizeDisabled,
                'no-preview'        : data.noPreview
            });

            const isReadOnly = store.getReadOnly();
            const alignResizeAvailable = !isReadOnly && !data.noPreview
            // const alignResizeAvailable = !isReadOnly && (!data.fileType || [
            //     gd.const.attachment.fileType.IMAGE,
            //     gd.const.attachment.fileType.GRAPHIC_SOURCE
            // ].includes(data.fileType))

            // WrappedComponent = Attachment in rich editor

            // const attachment = this.props.blockProps.attachmentsCollection ? this.props.blockProps.attachmentsCollection.get(data.fileId) : null;

            let attachment

            if (this.props.blockProps.attachmentsCollection) {
                // this is GoodDay Attachment
                attachment = this.props.blockProps.attachmentsCollection.get(data.fileId)
            } else {
                // this is case when user inserted image as link to an image (insert image)
                attachment = null
            }


            return (
                <WrappedComponent ref={element => {this.wrapper = element}}
                                  className     = {classes}
                                  style         = {styles}
                                  onRemove      = {this.onRemove} // make suere it's in elementProps = _.omit(this.props, ['preventScroll'... as well
                                  isReadOnly    = {isReadOnly}
                                  {...elementProps}
                                  {...interactionProps}>
                    {alignResizeAvailable &&
                    <Fragment>
                        <ResizeBorder/>

                        {!this.state.clicked &&
                            <AlignSelector alignment            = {this.props.blockProps.resizeData.alignment}
                                           attachment           = {attachment}
                                           onChangeAlignment    = {this.onChangeAlignment}
                                           onRemove             = {this.onRemove}/>}
                    </Fragment>}

                </WrappedComponent>
            );
        }
    };